import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { startImpersonation } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AdminWhatsAppPanel from "@/components/AdminWhatsAppPanel";
import AdminBroadcastPanel from "@/components/AdminBroadcastPanel";
import AdminOwnerDeclarationsPanel from "@/components/AdminOwnerDeclarationsPanel";
import AdminImpersonationPanel from "@/components/AdminImpersonationPanel";
import AdminVisitsPanel from "@/components/AdminVisitsPanel";
import AdminLegalAcceptancesPanel from "@/components/AdminLegalAcceptancesPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  CheckCircle2, X, Users, Home as HomeIcon, ShieldCheck, IndianRupee,
  Calendar, Landmark, FileText, Search, Copy, Plus, Trash2, Loader2, Download,
  UserCog,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
function fmtINR(n) { return `₹${(Number(n) || 0).toLocaleString("en-IN")}`; }

function StatusPill({ value }) {
  const map = {
    paid: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    verified: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    reserved: "bg-sky-100 text-sky-800",
    cancelled: "bg-slate-200 text-slate-700",
    not_submitted: "bg-slate-100 text-slate-600",
  };
  const cls = map[value] || "bg-slate-100 text-slate-700";
  return <span className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${cls}`}>{String(value || "—").replace("_", " ")}</span>;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pendingHostels, setPendingHostels] = useState([]);
  const [pendingKyc, setPendingKyc] = useState([]);
  const [pendingBanks, setPendingBanks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allHostels, setAllHostels] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [payTarget, setPayTarget] = useState(null);
  const [payUtr, setPayUtr] = useState("");
  const [payNote, setPayNote] = useState("");
  const [payLoading, setPayLoading] = useState(false);
  const [docPreview, setDocPreview] = useState(null);
  const [q, setQ] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetKeepIds, setResetKeepIds] = useState(new Set());
  const [resetForce, setResetForce] = useState(false);
  const [resetSkipRefunds, setResetSkipRefunds] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newHostel, setNewHostel] = useState({
    name: "", city: "", address: "", gender: "boys",
    price_per_bed: 300, total_beds: 10,
    amenities: "wifi,food,study",
    description: "",
    owner_email: "",
    owner_phone: "",
    ac_available: false, ac_price_per_bed: 0,
    meal_available: false, meal_price: 0, meal_description: "",
    property_document_type: "ownership",
  });

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/pending-hostels").then((r) => setPendingHostels(r.data));
    api.get("/admin/pending-kyc").then((r) => setPendingKyc(r.data));
    api.get("/admin/pending-banks").then((r) => setPendingBanks(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setAllUsers(r.data)).catch(() => {});
    api.get("/admin/hostels").then((r) => setAllHostels(r.data)).catch(() => {});
    api.get("/admin/uploads").then((r) => setUploads(r.data.groups || [])).catch(() => {});
    api.get("/admin/withdrawals").then((r) => setWithdrawals(r.data)).catch(() => {});
    api.get("/admin/boosts").then((r) => setBoosts(r.data.active || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = async (kind, id, action) => {
    try {
      await api.post(`/admin/${kind}/${id}/${action}`);
      toast.success(`${kind} ${action}d`);
      load();
    } catch (e) { toast.error("Action failed"); }
  };

  const copy = (v) => { navigator.clipboard.writeText(String(v || "")); toast.success("Copied"); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/hostels/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.name}" deleted · ${data.bookings_deleted} booking(s), ${data.reviews_deleted} review(s) removed`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  const confirmDeleteUser = async () => {
    if (!deleteUserTarget) return;
    setDeletingUser(true);
    try {
      const { data } = await api.delete(`/admin/users/${deleteUserTarget.id}`);
      toast.success(
        `"${deleteUserTarget.email}" deleted · ${data.hostels_deleted} hostel(s), ${data.bookings_deleted} booking(s), ${data.uploads_deleted} upload(s) removed`
      );
      setDeleteUserTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeletingUser(false); }
  };

  const impersonateUser = async (u) => {
    if (!window.confirm(
      `Act as "${u.name || u.email}" (${u.role})?\n\n` +
      `You will be redirected to their dashboard and can see exactly what they see, ` +
      `manage their listings, and troubleshoot issues on their behalf. A banner will let you exit and ` +
      `return to admin any time. The session lasts 1 hour and every action you take will be logged ` +
      `as "by admin on behalf of ${u.name || u.email}".`
    )) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/impersonate`);
      startImpersonation(data.token, data.user, data.impersonation);
      toast.success(`Now acting as ${data.user.name || data.user.email}`);
      const dest = data.user.role === "owner" ? "/owner" : "/dashboard";
      window.location.assign(dest);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonate failed");
    }
  };

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel booking for ${b.guest_email} at ${b.hostel_name}?\n\nThis marks the booking as cancelled (no refund needed — it was never paid).`)) return;
    try {
      await api.post(`/admin/bookings/${b.id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  const refundBooking = async (b) => {
    const msg =
      `Refund ₹${b.amount} to ${b.guest_email} for "${b.hostel_name}"?\n\n` +
      `• Razorpay will refund the payment (usually 3–5 business days).\n` +
      `• The bed(s) will be returned to inventory.\n` +
      `• The booking will be marked as cancelled.\n\n` +
      `This action cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/refund`);
      toast.success(data.already_refunded ? `Already refunded · ${data.refund_id}` : `Refund initiated · ${data.refund_id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    }
  };

  const reconcileBooking = async (b) => {
    if (!window.confirm(
      `Check Razorpay for booking ${b.id.slice(0,8)}?\n\n` +
      `If the student's payment was actually captured but stuck in "pending", this will mark the booking as PAID, decrement inventory, send the verification code + emails.\n\nSafe to click — no-op if payment was never captured.`
    )) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/reconcile`);
      if (data.already_paid) {
        toast.success("Already paid — no change.");
      } else if (data.ok && data.payment_ref) {
        toast.success(
          `Recovered! Verification ${data.verification_code} · Emails sent (student=${data.email_sent_to_student ? '✓' : '✗'}, owner=${data.email_sent_to_owner ? '✓' : '✗'})${!data.inventory_recovered ? ' · ⚠ inventory conflict' : ''}`,
          { duration: 8000 }
        );
      } else {
        toast.message(data.message || "No captured payment found on Razorpay.");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconcile failed");
    }
  };

  const runReset = async () => {
    if (resetConfirmText !== "RESET") return toast.error('Type RESET to confirm.');
    setResetting(true);
    try {
      // SKIP-REFUNDS: hard delete only, no Razorpay calls. Use for demo/test data.
      if (resetSkipRefunds) {
        const { data } = await api.post("/admin/reset-platform", {
          keep_hostel_ids: Array.from(resetKeepIds),
          force_refund_paid: false,
          skip_refunds: true,
          confirm: "RESET",
        });
        toast.success(`Purged (no refunds) · ${data.hostels_deleted} hostel(s), ${data.bookings_deleted} booking(s) removed`);
        setResetOpen(false);
        load();
        return;
      }

      // 1) Preflight — see which paid bookings need refunding first
      const { data: plan } = await api.post("/admin/reset-preflight", {
        keep_hostel_ids: Array.from(resetKeepIds),
        force_refund_paid: false,
        confirm: "RESET",
      });
      const paidToRefund = plan.paid_bookings_needing_refund || [];

      if (paidToRefund.length > 0 && !resetForce) {
        toast.error(`${paidToRefund.length} paid booking(s) need refund — tick 'Auto-refund' or 'Skip refunds' first.`);
        setResetting(false);
        return;
      }

      // 2) Chunk refunds through the fast per-booking endpoint
      let done = 0;
      for (const b of paidToRefund) {
        try {
          await api.post(`/admin/bookings/${b.id}/refund`);
        } catch (err) {
          toast.error(`Refund failed for ${b.guest_email}: ${err.response?.data?.detail || 'unknown'} — aborting reset.`);
          setResetting(false);
          return;
        }
        done++;
        toast.message(`Refunding… ${done}/${paidToRefund.length}`, { id: "reset-progress" });
      }

      // 3) All refunds done — now purge
      const { data } = await api.post("/admin/reset-platform", {
        keep_hostel_ids: Array.from(resetKeepIds),
        force_refund_paid: false,
        confirm: "RESET",
      });
      toast.success(`Purged · ${data.hostels_deleted} hostel(s), ${data.bookings_deleted} booking(s), ${done} refund(s)`, { id: "reset-progress" });
      setResetOpen(false);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.detail || "Reset failed"));
    } finally { setResetting(false); }
  };

  const saveNewHostel = async () => {
    // Basic guardrails
    if (!newHostel.name.trim() || !newHostel.city.trim() || !newHostel.address.trim()) {
      return toast.error("Name, city and address are required.");
    }
    if (Number(newHostel.price_per_bed) < 100 || Number(newHostel.price_per_bed) > 5000) {
      return toast.error("Price per bed must be between ₹100 and ₹5000.");
    }
    if (Number(newHostel.total_beds) < 1) {
      return toast.error("Total beds must be at least 1.");
    }
    if (newHostel.ac_available && Number(newHostel.ac_price_per_bed) <= Number(newHostel.price_per_bed)) {
      return toast.error("A/C price must be higher than Non-A/C price.");
    }
    if (newHostel.meal_available && (Number(newHostel.meal_price) < 100 || Number(newHostel.meal_price) > 250)) {
      return toast.error("Meal price must be between ₹100 and ₹250 per bed per night.");
    }
    setSaving(true);
    try {
      const payload = {
        name: newHostel.name.trim(),
        city: newHostel.city.trim(),
        address: newHostel.address.trim(),
        gender: newHostel.gender,
        price_per_bed: Number(newHostel.price_per_bed),
        total_beds: Number(newHostel.total_beds),
        amenities: newHostel.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        description: newHostel.description,
        owner_email: newHostel.owner_email.trim() || null,
        owner_phone: newHostel.owner_phone.trim() || "",
        ac_available: !!newHostel.ac_available,
        ac_price_per_bed: Number(newHostel.ac_price_per_bed) || 0,
        meal_available: !!newHostel.meal_available,
        meal_price: Number(newHostel.meal_price) || 0,
        meal_description: newHostel.meal_description || "",
        property_document_type: newHostel.property_document_type,
        authority_confirmed: true,
        coaching_centers: [],
        images: [],
      };
      await api.post("/admin/hostels", payload);
      toast.success(`"${payload.name}" added — live immediately.`);
      setAddOpen(false);
      setNewHostel({
        name: "", city: "", address: "", gender: "boys",
        price_per_bed: 300, total_beds: 10,
        amenities: "wifi,food,study",
        description: "", owner_email: "", owner_phone: "",
        ac_available: false, ac_price_per_bed: 0,
        meal_available: false, meal_price: 0, meal_description: "",
        property_document_type: "ownership",
      });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Create failed. Check the fields.");
    } finally { setSaving(false); }
  };

  const matches = (row, keys) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return keys.some((k) => String(row?.[k] || "").toLowerCase().includes(needle));
  };
  const [bookingStatusFilter, setBookingStatusFilter] = useState("all");
  const filteredBookings = useMemo(
    () => bookings
      .filter((b) => bookingStatusFilter === "all" ? true : (b.payment_status || "pending") === bookingStatusFilter)
      .filter((b) => matches(b, ["guest_name", "guest_email", "guest_phone", "hostel_name", "hostel_city", "owner_name", "id"])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookings, q, bookingStatusFilter]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredUsers = useMemo(() => allUsers.filter((u) => matches(u, ["name", "email", "phone", "role", "kyc_status"])), [allUsers, q]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredHostels = useMemo(() => allHostels.filter((h) => matches(h, ["name", "city", "address", "owner_name", "owner_email", "status"])), [allHostels, q]);
  const filteredUploads = useMemo(() => {
    const q1 = (q || "").toLowerCase().trim();
    if (!q1) return uploads;
    return uploads
      .map((g) => {
        const groupMatch = ["name", "email", "phone", "role"].some((k) => (g[k] || "").toLowerCase().includes(q1));
        if (groupMatch) return g;
        const files = (g.files || []).filter((f) => [f.label, f.doc_type, f.hostel_name, f.hostel_city].some((v) => (v || "").toLowerCase().includes(q1)));
        return files.length ? { ...g, files } : null;
      })
      .filter(Boolean);
  }, [uploads, q]);
  const totalFilesShown = useMemo(() => filteredUploads.reduce((n, g) => n + (g.files?.length || 0), 0), [filteredUploads]);

  return (
    <div className="min-h-screen bg-[#FBF5EA]" data-testid="admin-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600">Admin Console</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-1">Platform overview</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">Full oversight of who&apos;s booking, who&apos;s listing, and every file uploaded on HostelNow.</p>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-6 gap-4">
          {stats && [
            { icon: Users, label: "Users", value: stats.users, cls: "bg-orange-100 text-orange-800" },
            { icon: HomeIcon, label: "Hostels", value: stats.hostels, cls: "bg-amber-100 text-amber-800" },
            { icon: Calendar, label: "Bookings", value: stats.bookings, cls: "bg-emerald-100 text-emerald-700" },
            { icon: IndianRupee, label: "Revenue", value: fmtINR(stats.revenue), cls: "bg-stone-900 text-white" },
            { icon: HomeIcon, label: "Pending hostels", value: stats.pending_hostels, cls: "bg-amber-100 text-amber-700" },
            { icon: ShieldCheck, label: "Pending KYC", value: stats.pending_kyc, cls: "bg-rose-100 text-rose-700" },
            { icon: Landmark, label: "Pending banks", value: stats.pending_banks || 0, cls: "bg-amber-100 text-amber-800" },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className={`w-9 h-9 rounded-lg grid place-items-center ${s.cls}`}><s.icon size={16} /></div>
              <div className="mt-3 text-[10px] tracking-widest uppercase font-bold text-slate-500">{s.label}</div>
              <div className="font-display font-black text-xl mt-0.5">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 relative max-w-lg">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="admin-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search across bookings, users, hostels, uploads…"
            className="pl-9 bg-white rounded-full"
          />
        </div>

        <div className="mt-6">
          <AdminWhatsAppPanel />
        </div>

        <Tabs defaultValue="bookings" className="mt-6">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="visits" data-testid="admin-tab-visits">Visits</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users ({allUsers.length})</TabsTrigger>
            <TabsTrigger value="all-hostels" data-testid="admin-tab-all-hostels">Hostels ({allHostels.length})</TabsTrigger>
            <TabsTrigger value="uploads" data-testid="admin-tab-uploads">Uploads ({uploads.reduce((n, g) => n + (g.files?.length || 0), 0)})</TabsTrigger>
            <TabsTrigger value="hostels" data-testid="admin-tab-hostels">Pending Hostels ({pendingHostels.length})</TabsTrigger>
            <TabsTrigger value="kyc" data-testid="admin-tab-kyc">Pending KYC ({pendingKyc.length})</TabsTrigger>
            <TabsTrigger value="banks" data-testid="admin-tab-banks">Pending Banks ({pendingBanks.length})</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="admin-tab-payouts">Payouts ({withdrawals.filter(w => w.status === 'pending').length})</TabsTrigger>
            <TabsTrigger value="boosts" data-testid="admin-tab-boosts">Boosts ({boosts.length})</TabsTrigger>
            <TabsTrigger value="declarations" data-testid="admin-tab-declarations">Declarations</TabsTrigger>
            <TabsTrigger value="student-declarations" data-testid="admin-tab-student-declarations">Student Declarations</TabsTrigger>
            <TabsTrigger value="broadcast" data-testid="admin-tab-broadcast">Broadcast</TabsTrigger>
            <TabsTrigger value="impersonation" data-testid="admin-tab-impersonation">Impersonation</TabsTrigger>
          </TabsList>

          {/* ---------- BOOKINGS ---------- */}
          <TabsContent value="bookings" className="mt-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-xs uppercase tracking-widest font-bold text-slate-500 mr-1">Filter:</span>
              {[
                { key: "all", label: `All (${bookings.length})` },
                { key: "pending", label: `Pending (${bookings.filter(b => b.payment_status === "pending").length})`, hint: "Orphaned — payment may have been captured on Razorpay" },
                { key: "paid", label: `Paid (${bookings.filter(b => b.payment_status === "paid").length})` },
                { key: "refunded", label: `Refunded (${bookings.filter(b => b.payment_status === "refunded").length})` },
                { key: "refunded_oversold", label: `Oversold (${bookings.filter(b => b.payment_status === "refunded_oversold").length})` },
              ].map((f) => (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`booking-filter-${f.key}`}
                  onClick={() => setBookingStatusFilter(f.key)}
                  title={f.hint || ""}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors border ${
                    bookingStatusFilter === f.key
                      ? "bg-vermilion-600 text-white border-vermilion-600"
                      : "bg-white border-slate-200 text-slate-700 hover:border-vermilion-400"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {filteredBookings.length === 0 && <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">No bookings match this filter.</div>}
            <div className="space-y-3">
              {filteredBookings.map((b) => (
                <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 grid md:grid-cols-[auto_1fr_auto] gap-4 items-center">
                  <img src={b.hostel_image} alt="" className="w-full md:w-24 h-20 object-cover rounded-xl" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold truncate">{b.hostel_name}</div>
                      <span className="text-xs text-slate-500">· {b.hostel_city}</span>
                      <StatusPill value={b.payment_status} />
                      <StatusPill value={b.status} />
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      Guest: <span className="font-semibold text-slate-800">{b.guest_name || "—"}</span> · {b.guest_email || "—"} · {b.guest_phone || "—"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Owner: {b.owner_name || "—"} ({b.owner_email || "—"})
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {b.beds} bed(s) · {b.room_type || "Non-A/C"} · {b.nights} night(s) · {b.check_in} → {b.check_out} · {b.meal_included ? "with meals" : "no meals"} · Booked {fmtDate(b.created_at)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-lg">{fmtINR(b.amount)}</div>
                    {(b.platform_fee || 0) > 0 && <div className="text-[10px] text-slate-500">Platform fee {fmtINR(b.platform_fee)}</div>}
                    <div className="mt-2 flex flex-wrap justify-end gap-1.5">
                      {b.payment_status === "paid" && b.status !== "cancelled" && (
                        <Button
                          data-testid={`refund-booking-${b.id}`}
                          size="sm"
                          onClick={() => refundBooking(b)}
                          className="rounded-full bg-rose-600 hover:bg-rose-700 text-white h-8 text-xs gap-1"
                        >
                          <IndianRupee size={12} /> Refund & cancel
                        </Button>
                      )}
                      {b.payment_status === "pending" && b.razorpay_order_id && (
                        <Button
                          data-testid={`reconcile-booking-${b.id}`}
                          size="sm"
                          onClick={() => reconcileBooking(b)}
                          className="rounded-full bg-peacock-600 hover:bg-peacock-700 text-white h-8 text-xs gap-1"
                          title="Check Razorpay: if the payment was actually captured, mark this booking as PAID and send emails"
                        >
                          <ShieldCheck size={12} /> Reconcile with Razorpay
                        </Button>
                      )}
                      {b.payment_status !== "paid" && b.status !== "cancelled" && (
                        <Button
                          data-testid={`cancel-booking-${b.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => cancelBooking(b)}
                          className="rounded-full h-8 text-xs gap-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                        >
                          <X size={12} /> Cancel
                        </Button>
                      )}
                      {b.refund_id && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800" title={b.refund_id}>
                          Refund · {String(b.refund_id).slice(-8)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ---------- USERS ---------- */}
          <TabsContent value="users" className="mt-6">
            {filteredUsers.length === 0 && <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">No users match.</div>}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="py-3 px-4">Name</th>
                    <th className="py-3 px-4">Contact</th>
                    <th className="py-3 px-4">Role</th>
                    <th className="py-3 px-4">KYC</th>
                    <th className="py-3 px-4">Joined</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100">
                      <td className="py-3 px-4">
                        <div className="font-semibold">{u.name}</div>
                        <div className="text-[10px] font-mono text-slate-400">{u.id}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">{u.email} <button onClick={() => copy(u.email)} className="text-slate-400 hover:text-slate-700"><Copy size={12}/></button></div>
                        <div className="text-xs text-slate-500">{u.phone || "—"}</div>
                      </td>
                      <td className="py-3 px-4"><Badge variant="outline" className="capitalize">{u.role}</Badge></td>
                      <td className="py-3 px-4">
                        <StatusPill value={u.kyc_status} />
                        {u.role === "owner" && (() => {
                          const ownerHostels = allHostels.filter((h) => h.owner_id === u.id);
                          if (ownerHostels.length === 0) return null;
                          return (
                            <details data-testid={`owner-hostels-${u.id}`} className="mt-1">
                              <summary className="text-[10px] text-orange-800 font-semibold cursor-pointer hover:underline">
                                {ownerHostels.length} hostel{ownerHostels.length === 1 ? "" : "s"} ▾
                              </summary>
                              <ul className="mt-1 text-[10px] text-slate-600 space-y-0.5 pl-2 border-l-2 border-slate-200">
                                {ownerHostels.map((h) => (
                                  <li key={h.id} className="truncate max-w-[220px]" title={h.name}>
                                    · {h.name} · {h.city}
                                    <span className={`ml-1 text-[9px] font-bold uppercase px-1 rounded ${h.status === "approved" ? "bg-emerald-100 text-emerald-700" : h.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{h.status}</span>
                                    {h.deleted_at && <span className="ml-1 text-rose-600">(removed)</span>}
                                    {h.available === false && !h.deleted_at && <span className="ml-1 text-slate-500">(paused)</span>}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500">{fmtDate(u.created_at)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                          {u.role !== "admin" && (
                            <Button
                              data-testid={`impersonate-user-${u.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => impersonateUser(u)}
                              className="rounded-full text-orange-800 border-orange-300 hover:bg-orange-50 gap-1"
                              title={`Act as ${u.name || u.email} to debug their ${u.role} dashboard`}
                            >
                              <UserCog size={12} /> {u.role === "owner" ? "Act as this Owner" : "Act as this User"}
                            </Button>
                          )}
                          {u.role !== "admin" && (
                            <Button
                              data-testid={`delete-user-${u.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteUserTarget(u)}
                              className="rounded-full text-rose-700 border-rose-300 hover:bg-rose-50 gap-1"
                            >
                              <Trash2 size={12} /> Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* ---------- ALL HOSTELS ---------- */}
          <TabsContent value="all-hostels" className="mt-6 space-y-3">
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm text-slate-600">{filteredHostels.length} listings</div>
              <Button
                data-testid="admin-add-hostel-btn"
                onClick={() => setAddOpen(true)}
                className="rounded-full bg-orange-800 hover:bg-orange-900 text-white gap-1"
              >
                <Plus size={16} /> Add hostel
              </Button>
            </div>
            {filteredHostels.length === 0 && <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">No hostels match.</div>}
            {filteredHostels.map((h) => (
              <div key={h.id} data-testid={`hostel-row-${h.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row gap-4 items-start md:items-center">
                <img src={h.images?.[0]} className="w-full md:w-32 h-24 object-cover rounded-xl" alt="" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-bold text-lg truncate">{h.name}</div>
                    <StatusPill value={h.status} />
                  </div>
                  <div className="text-sm text-slate-600">{h.city} · {h.address}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Owner: <span className="font-semibold text-slate-700">{h.owner_name || "—"}</span> · {h.owner_email || "—"} · {h.owner_phone || "—"}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {fmtINR(h.price_per_bed)}/bed · {h.total_beds} beds · {h.gender} · A/C: {h.ac_available ? "yes" : "no"} · Meals: {h.meal_available ? "yes" : "no"} · Listed {fmtDate(h.created_at)}
                  </div>
                  {Array.isArray(h.room_types) && h.room_types.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`hostel-room-types-${h.id}`}>
                      <span className="text-[10px] tracking-widest uppercase font-bold text-slate-400 self-center">Rooms:</span>
                      {h.room_types.map((rt) => (
                        <span
                          key={rt.id}
                          data-testid={`hostel-room-type-chip-${rt.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-marigold-50 border border-marigold-300 px-2 py-0.5 text-[11px]"
                        >
                          <b className="text-vermilion-700">{rt.name}</b>
                          <span className="text-slate-500">·</span>
                          <span className="font-semibold">{fmtINR(rt.price_per_month)}/mo</span>
                          <span className="text-slate-500">·</span>
                          <span className={Number(rt.rooms_available) === 0 ? "text-rose-600 font-semibold" : "text-emerald-700"}>{rt.rooms_available}/{rt.rooms_total}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  data-testid={`delete-hostel-${h.id}`}
                  variant="outline"
                  onClick={() => setDeleteTarget(h)}
                  className="rounded-full text-rose-700 border-rose-300 hover:bg-rose-50 gap-1"
                >
                  <Trash2 size={14} /> Delete
                </Button>
              </div>
            ))}
          </TabsContent>

          {/* ---------- UPLOADS ---------- */}
          <TabsContent value="uploads" className="mt-6">
            <div className="mb-3 text-xs text-slate-500">
              Every asset uploaded on the platform, grouped by the owner or student who uploaded it. Aadhaar / PAN / property proof docs are admin-only.
              Showing <b>{totalFilesShown}</b> file{totalFilesShown === 1 ? "" : "s"} across <b>{filteredUploads.length}</b> account{filteredUploads.length === 1 ? "" : "s"}.
            </div>
            {filteredUploads.length === 0 && (
              <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">No uploads match your search.</div>
            )}
            <div className="space-y-4">
              {filteredUploads.map((g) => {
                const hostelIds = [...new Set((g.files || []).filter(f => f.hostel_id).map(f => f.hostel_id))];
                return (
                  <div key={g.user_id} data-testid={`upload-group-${g.user_id}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="px-5 py-3 bg-gradient-to-br from-marigold-50 to-turmeric-50 border-b border-marigold-200 flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-base">{g.name || "(deleted user)"}</div>
                          <Badge variant="outline" className="capitalize text-[10px]">{g.role}</Badge>
                          {g.kyc_status && <StatusPill value={g.kyc_status} />}
                        </div>
                        <div className="text-xs text-slate-600">{g.email} · {g.phone || "no phone"}</div>
                      </div>
                      <div className="text-[11px] text-slate-500">{g.files.length} file{g.files.length === 1 ? "" : "s"}</div>
                    </div>

                    {/* Hostel-level Approve / Reject bar for this owner */}
                    {hostelIds.map((hid) => {
                      const h = allHostels.find(x => x.id === hid);
                      if (!h) return null;
                      return (
                        <div key={hid} className="px-5 py-2 border-b border-slate-100 bg-slate-50 flex items-center flex-wrap gap-3">
                          <div className="flex-1 min-w-[200px] text-sm">
                            <span className="font-semibold">{h.name}</span> · <span className="text-slate-500">{h.city}</span>{" "}
                            <Badge className={`ml-1 ${h.status === "approved" ? "bg-emerald-100 text-emerald-700" : h.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{h.status}</Badge>
                          </div>
                          {h.status !== "approved" && (
                            <Button data-testid={`uploads-approve-${hid}`} onClick={() => act("hostels", hid, "approve")} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1"><CheckCircle2 size={13} /> Approve listing</Button>
                          )}
                          {h.status !== "rejected" && (
                            <Button data-testid={`uploads-reject-${hid}`} onClick={() => {
                              const reason = window.prompt(`Reject "${h.name}" — reason (owner will get this via email):`, "Documents don't meet our verification standards. Please upload clearer photos of the property ownership document and your Aadhaar, then resubmit.");
                              if (!reason) return;
                              api.post(`/admin/hostels/${hid}/reject`, { reason })
                                .then((r) => { toast.success(`Rejected · ${r.data.email_sent_to_owner ? "email sent to owner ✓" : "owner has no email"}`); load(); })
                                .catch((e) => toast.error(e.response?.data?.detail || "Reject failed"));
                            }} variant="outline" size="sm" className="rounded-full text-rose-700 border-rose-300 hover:bg-rose-50 gap-1"><X size={13} /> Reject with reason</Button>
                          )}
                        </div>
                      );
                    })}

                    {/* Files gallery */}
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {g.files.map((f) => {
                        const isImage = (f.content_type || "").startsWith("image/");
                        const href = `${process.env.REACT_APP_BACKEND_URL}${f.url}?auth=${encodeURIComponent(localStorage.getItem("hn_token") || "")}`;
                        const badgeColor = f.category === "kyc" ? "bg-rose-100 text-rose-800 border-rose-200"
                          : f.category === "property" ? "bg-jodhpur-100 text-jodhpur-700 border-jodhpur-200"
                          : f.category === "hostel" ? "bg-turmeric-100 text-vermilion-700 border-turmeric-300"
                          : "bg-slate-100 text-slate-700 border-slate-200";
                        return (
                          <div key={f.id} data-testid={`upload-file-${f.id}`} className="border border-slate-200 rounded-xl overflow-hidden group">
                            <a href={href} target="_blank" rel="noopener noreferrer" className="block aspect-[4/3] bg-slate-100 relative overflow-hidden">
                              {isImage ? (
                                <img src={href} alt={f.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                              ) : (
                                <div className="w-full h-full grid place-items-center text-slate-400 flex-col">
                                  <FileText size={28} />
                                  <div className="text-[10px] uppercase mt-1 tracking-widest">{(f.content_type || "file").split("/")[1] || "file"}</div>
                                </div>
                              )}
                              <span className={`absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded border ${badgeColor}`}>{f.label}</span>
                            </a>
                            <div className="p-2 flex items-center justify-between gap-1">
                              <div className="min-w-0 flex-1">
                                <div className="text-[10px] text-slate-500 truncate">{f.hostel_name || (f.category === "kyc" ? "Identity" : "Document")}</div>
                                <div className="text-[10px] text-slate-400">{Math.round((f.size || 0) / 1024)} KB · {fmtDate(f.created_at)}</div>
                              </div>
                              <a href={href} download title="Download" className="text-slate-500 hover:text-vermilion-600 p-1"><Download size={13} /></a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* ---------- PENDING HOSTELS ---------- */}
          <TabsContent value="hostels" className="mt-6 space-y-3">
            {pendingHostels.length === 0 && <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">All caught up — no hostels awaiting approval.</div>}
            {pendingHostels.map((h) => (
              <div key={h.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row gap-4 items-center">
                <img src={h.images?.[0]} className="w-full md:w-40 h-28 object-cover rounded-xl" alt="" />
                <div className="flex-1">
                  <div className="font-display font-bold text-lg">{h.name}</div>
                  <div className="text-sm text-slate-500">{h.city} · {h.address}</div>
                  <div className="text-sm mt-1">₹{h.price_per_bed}/bed · {h.total_beds} beds · {h.gender}</div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`approve-hostel-${h.id}`} onClick={() => act("hostels", h.id, "approve")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1"><CheckCircle2 size={16} /> Approve</Button>
                  <Button variant="outline" onClick={() => act("hostels", h.id, "reject")} className="rounded-full text-rose-700 border-rose-300 gap-1"><X size={16} /> Reject</Button>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* ---------- PENDING KYC ---------- */}
          <TabsContent value="kyc" className="mt-6 space-y-3">
            {pendingKyc.length === 0 && <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">No pending KYC submissions.</div>}
            {pendingKyc.map((u) => {
              const isOwner = u.role === "owner";
              const aadhaarFileId = isOwner ? u.kyc?.aadhaar_file_id : u.kyc?.student_aadhaar_file_id;
              const aadhaarNum = isOwner ? u.kyc?.aadhaar_number : u.kyc?.student_aadhaar_number;
              const authT = localStorage.getItem("hn_token") || "";
              const aadhaarUrl = aadhaarFileId ? `${API}/kyc/file/${aadhaarFileId}?auth=${authT}` : "";
              const panUrl = u.kyc?.pan_file_id ? `${API}/kyc/file/${u.kyc.pan_file_id}?auth=${authT}` : "";
              return (
                <div key={u.id} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
                  <div className="flex flex-col md:flex-row gap-4 items-start">
                    <div className="w-12 h-12 rounded-full bg-orange-100 text-orange-800 grid place-items-center font-bold flex-shrink-0">
                      {u.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">
                        {u.name} <span className="text-[10px] uppercase font-bold ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{u.role}</span>
                      </div>
                      <div className="text-xs text-slate-500">{u.email} · {u.phone}</div>
                      <div className="text-xs mt-2 text-slate-700">
                        Aadhaar: <span className="font-mono">{aadhaarNum || "—"}</span>
                        {isOwner && u.kyc?.pan_number && <> · PAN: <span className="font-mono">{u.kyc.pan_number}</span></>}
                        {!isOwner && u.kyc?.guardian_name && <> · Guardian: {u.kyc.guardian_name} ({u.kyc.guardian_phone})</>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button data-testid={`approve-kyc-${u.id}`} onClick={() => act("kyc", u.id, "approve")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1"><CheckCircle2 size={16} /> Verify</Button>
                      <Button
                        data-testid={`reject-kyc-${u.id}`}
                        variant="outline"
                        onClick={async () => {
                          const reason = window.prompt("Why is this KYC being rejected?\n(This will be shown to the student so they can fix and re-submit)", "Aadhaar photo unclear — please re-upload");
                          if (reason === null) return;
                          try {
                            await api.post(`/admin/kyc/${u.id}/reject`, { reason });
                            toast.success("KYC rejected · user notified");
                            load();
                          } catch { toast.error("Reject failed"); }
                        }}
                        className="rounded-full text-rose-700 border-rose-300 gap-1"
                      >
                        <X size={16} /> Reject
                      </Button>
                    </div>
                  </div>

                  {/* Document thumbnails — inline preview so admin can verify without leaving page */}
                  <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-dashed border-slate-200">
                    {aadhaarUrl ? (
                      <button
                        data-testid={`aadhaar-thumb-${u.id}`}
                        type="button"
                        onClick={() => setDocPreview({ url: aadhaarUrl, label: "Aadhaar", user: u.name })}
                        className="text-left group"
                      >
                        <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 mb-1 flex items-center gap-1"><FileText size={10} /> Aadhaar document</div>
                        <div className="relative w-full h-40 rounded-xl border-2 border-slate-200 bg-slate-100 overflow-hidden group-hover:border-orange-600 transition-colors">
                          <img src={aadhaarUrl} alt="Aadhaar" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 grid place-items-center transition-colors">
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-bold px-3 py-1 rounded-full bg-black/60">Click to enlarge</span>
                          </div>
                        </div>
                      </button>
                    ) : (
                      <div className="rounded-xl border-2 border-dashed border-slate-300 h-40 grid place-items-center text-xs text-slate-400">No Aadhaar file uploaded</div>
                    )}
                    {panUrl ? (
                      <button
                        data-testid={`pan-thumb-${u.id}`}
                        type="button"
                        onClick={() => setDocPreview({ url: panUrl, label: "PAN", user: u.name })}
                        className="text-left group"
                      >
                        <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 mb-1 flex items-center gap-1"><FileText size={10} /> PAN document</div>
                        <div className="relative w-full h-40 rounded-xl border-2 border-slate-200 bg-slate-100 overflow-hidden group-hover:border-orange-600 transition-colors">
                          <img src={panUrl} alt="PAN" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 grid place-items-center transition-colors">
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-bold px-3 py-1 rounded-full bg-black/60">Click to enlarge</span>
                          </div>
                        </div>
                      </button>
                    ) : isOwner ? (
                      <div className="rounded-xl border-2 border-dashed border-slate-300 h-40 grid place-items-center text-xs text-slate-400">PAN not provided (optional)</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* ---------- PENDING BANKS ---------- */}
          <TabsContent value="banks" className="mt-6 space-y-3">
            {pendingBanks.length === 0 && <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">No pending bank verifications.</div>}
            {pendingBanks.map((u) => {
              const b = u.bank_account || {};
              return (
                <div key={u.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row gap-4 items-start md:items-center">
                  <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-800 grid place-items-center"><Landmark size={20} /></div>
                  <div className="flex-1">
                    <div className="font-semibold">{u.name} <span className="text-xs text-slate-500 font-normal">({u.role})</span></div>
                    <div className="text-xs text-slate-500">{u.email} · {u.phone}</div>
                    <div className="mt-2 text-xs text-slate-700">
                      <div><span className="font-semibold">Holder:</span> {b.account_holder}</div>
                      <div><span className="font-semibold">Bank:</span> {b.bank_name || "—"} · <span className="font-semibold">IFSC:</span> <span className="font-mono">{b.ifsc}</span></div>
                      <div><span className="font-semibold">A/C:</span> <span className="font-mono">{b.account_number}</span></div>
                      {b.upi_id && <div><span className="font-semibold">UPI:</span> {b.upi_id}</div>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`approve-bank-${u.id}`} onClick={() => act("bank", u.id, "verify")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1"><CheckCircle2 size={16} /> Verify</Button>
                    <Button variant="outline" onClick={() => act("bank", u.id, "reject")} className="rounded-full text-rose-700 border-rose-300 gap-1"><X size={16} /> Reject</Button>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* ---------- PAYOUTS (Manual withdrawal approvals) ---------- */}
          <TabsContent value="payouts" className="mt-6 space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
              <b>How this works:</b> When an owner requests a payout, it appears here as <b>pending</b>. Transfer the amount to the owner via UPI or IMPS using the bank details shown below, then click <b>Mark as paid</b> and enter the UTR / transaction reference. The owner will see the UTR in their withdrawal history.
            </div>
            {withdrawals.length === 0 && (
              <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">No payout requests yet.</div>
            )}
            {withdrawals.map((w) => {
              const isPending = w.status === "pending";
              const isPaid = w.status === "processed";
              const isRej = w.status === "rejected";
              const b = w.bank_account || {};
              return (
                <div key={w.id} data-testid={`admin-withdraw-${w.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row gap-4 items-start md:items-center">
                  <div className={`w-12 h-12 rounded-full grid place-items-center ${isPending ? "bg-amber-100 text-amber-700" : isPaid ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    <IndianRupee size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-display font-bold text-xl">{fmtINR(Math.round(w.net_amount))}</div>
                      <StatusPill value={w.status} />
                      <span className="text-xs text-slate-500">Ref <span className="font-mono">{w.reference}</span></span>
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      Owner: <span className="font-semibold text-slate-800">{w.owner_name || "—"}</span> · {w.owner_email || "—"} · {w.owner_phone || "—"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Gross {fmtINR(w.gross_amount)} · Fee {fmtINR(w.fee)} · {w.bookings_count} booking(s) · Requested {fmtDate(w.created_at)}
                    </div>
                    <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <div><span className="text-slate-500">Holder:</span> <span className="font-semibold">{b.account_holder || "—"}</span></div>
                      <div><span className="text-slate-500">Bank:</span> <span className="font-semibold">{b.bank_name || "—"}</span></div>
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500">A/C:</span> <span className="font-mono">{b.account_number || "—"}</span>
                        {b.account_number && <button onClick={() => copy(b.account_number)} className="text-orange-800 hover:text-orange-900"><Copy size={12} /></button>}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500">IFSC:</span> <span className="font-mono">{b.ifsc || "—"}</span>
                        {b.ifsc && <button onClick={() => copy(b.ifsc)} className="text-orange-800 hover:text-orange-900"><Copy size={12} /></button>}
                      </div>
                      {b.upi_id && (
                        <div className="flex items-center gap-1 col-span-2">
                          <span className="text-slate-500">UPI:</span> <span className="font-mono">{b.upi_id}</span>
                          <button onClick={() => copy(b.upi_id)} className="text-orange-800 hover:text-orange-900"><Copy size={12} /></button>
                        </div>
                      )}
                    </div>
                    {isPaid && (
                      <div className="mt-2 text-xs text-emerald-700">
                        Paid {fmtDate(w.processed_at)} by {w.processed_by} · UTR <span className="font-mono">{w.utr}</span>
                        {w.admin_note && <> · {w.admin_note}</>}
                      </div>
                    )}
                    {isRej && (
                      <div className="mt-2 text-xs text-rose-700">
                        Rejected {fmtDate(w.processed_at)} by {w.processed_by} · {w.rejection_reason}
                      </div>
                    )}
                  </div>
                  {isPending && (
                    <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                      <Button
                        data-testid={`mark-paid-${w.id}`}
                        onClick={() => { setPayTarget(w); setPayUtr(""); setPayNote(""); }}
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                      >
                        <CheckCircle2 size={16} /> Mark as paid
                      </Button>
                      <Button
                        data-testid={`reject-payout-${w.id}`}
                        variant="outline"
                        onClick={async () => {
                          const reason = window.prompt("Why is this payout being rejected?\n(Bookings will be un-settled so the owner can request again.)", "Bank details invalid");
                          if (reason === null) return;
                          try {
                            await api.post(`/admin/withdrawals/${w.id}/reject`, { reason });
                            toast.success("Payout rejected");
                            load();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Reject failed");
                          }
                        }}
                        className="rounded-full text-rose-700 border-rose-300 gap-1"
                      >
                        <X size={16} /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="boosts" className="mt-6 space-y-3" data-testid="boosts-panel">
            <div className="rounded-2xl border-2 border-turmeric-300 bg-gradient-to-br from-turmeric-50 to-white p-4">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-black tracking-widest uppercase text-jodhpur-900 bg-turmeric-300 rounded-full px-2 py-0.5">
                  Rocket 🚀 Featured listings
                </span>
                <span className="text-sm text-slate-600">
                  {boosts.length === 0 ? "No listings are currently boosted." : `${boosts.length} listing${boosts.length === 1 ? "" : "s"} boosted right now.`}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Boost prices: 7d ₹499 · 15d ₹899 · 30d ₹1,499 · paid via Razorpay · stacks on renewal.
              </div>
            </div>
            {boosts.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">No active boosts. Owners buy boosts from their dashboard.</div>
            )}
            {boosts.map((b) => {
              const until = b.featured_until ? new Date(b.featured_until) : null;
              const started = b.current_boost_started_at ? new Date(b.current_boost_started_at) : null;
              const daysLeft = until ? Math.max(0, Math.ceil((until - new Date()) / (1000 * 60 * 60 * 24))) : 0;
              return (
                <div key={b.hostel_id} data-testid={`admin-boost-row-${b.hostel_id}`} className="bg-white border border-slate-200 rounded-2xl p-4">
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold text-lg truncate">{b.hostel_name}</span>
                        <Badge className="bg-turmeric-400 text-jodhpur-900 gap-1">Featured · {daysLeft}d left</Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{b.city} · Owner: {b.owner_name || "—"} ({b.owner_email || "—"})</div>
                      <div className="mt-2 grid sm:grid-cols-4 gap-2 text-[11px]">
                        <div className="bg-slate-50 rounded-md px-2 py-1"><b>Latest boost</b><br />{b.current_boost_days || "?"}d · ₹{(b.current_boost_amount || 0).toLocaleString("en-IN")}</div>
                        <div className="bg-slate-50 rounded-md px-2 py-1"><b>Started</b><br />{started ? started.toLocaleString("en-IN") : "—"}</div>
                        <div className="bg-slate-50 rounded-md px-2 py-1"><b>Expires</b><br />{until ? until.toLocaleString("en-IN") : "—"}</div>
                        <div className="bg-slate-50 rounded-md px-2 py-1"><b>Total boost spend</b><br />₹{(b.total_boost_spend || 0).toLocaleString("en-IN")} · {b.boost_count || 0} purchase{b.boost_count === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="broadcast" className="mt-6" data-testid="broadcast-tab">
            <AdminBroadcastPanel />
          </TabsContent>

          <TabsContent value="declarations" className="mt-6" data-testid="declarations-tab">
            <AdminOwnerDeclarationsPanel />
          </TabsContent>

          <TabsContent value="student-declarations" className="mt-6" data-testid="student-declarations-tab">
            <AdminLegalAcceptancesPanel />
          </TabsContent>

          <TabsContent value="visits" className="mt-6" data-testid="visits-tab">
            <AdminVisitsPanel />
          </TabsContent>

          <TabsContent value="impersonation" className="mt-6" data-testid="impersonation-tab">
            <AdminImpersonationPanel />
          </TabsContent>
        </Tabs>

        {/* --- Danger zone: Reset platform --- */}
        <div className="mt-14 border-2 border-rose-300 bg-rose-50/40 rounded-2xl p-6" data-testid="danger-zone">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block text-[10px] uppercase tracking-widest font-black text-rose-800 bg-rose-100 border border-rose-300 rounded-full px-2 py-1">Danger zone</span>
            <span className="text-sm font-semibold text-rose-900">Reset platform data</span>
          </div>
          <p className="text-sm text-slate-700 max-w-2xl">
            Deletes <b>all bookings</b> and every hostel <b>except</b> the ones you select below. If any paid bookings exist on hostels being removed, they will be auto-refunded through Razorpay before deletion.
          </p>
          <Button
            data-testid="open-reset-platform-btn"
            onClick={() => { setResetKeepIds(new Set()); setResetForce(false); setResetSkipRefunds(false); setResetConfirmText(""); setResetOpen(true); }}
            className="mt-4 rounded-full bg-rose-700 hover:bg-rose-800 text-white gap-2"
          >
            <Trash2 size={14} /> Open reset tool
          </Button>
        </div>
      </div>

      {/* --- Delete confirmation --- */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this hostel?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-semibold text-slate-900">{deleteTarget?.name}</span> from {deleteTarget?.city}, along with all its unpaid bookings and reviews.
              <br /><br />
              <span className="text-rose-700 font-semibold">Bookings with completed payments cannot be deleted</span> — you&apos;ll need to cancel/refund them first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleting ? <><Loader2 className="animate-spin mr-1" size={14} /> Deleting…</> : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* --- Delete user confirmation --- */}
      <AlertDialog open={!!deleteUserTarget} onOpenChange={(v) => !v && setDeleteUserTarget(null)}>
        <AlertDialogContent data-testid="delete-user-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user account?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-semibold text-slate-900">{deleteUserTarget?.email}</span> ({deleteUserTarget?.role}) along with all their hostels, unpaid bookings, reviews, withdrawals and uploads.
              <br /><br />
              <span className="text-rose-700 font-semibold">Users with paid bookings cannot be deleted</span> — refund/cancel those first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingUser}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-user-confirm-btn"
              disabled={deletingUser}
              onClick={confirmDeleteUser}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deletingUser ? <><Loader2 className="animate-spin mr-1" size={14} /> Deleting…</> : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* --- Reset platform dialog --- */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="reset-platform-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-rose-800">Reset platform data</DialogTitle>
            <DialogDescription>
              This deletes every booking + review + hostel <b>except</b> the ones you tick to keep below.
              Auto-restores bed inventory on the ones you keep.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-900">
            <b>Money-safe:</b> If any hostel being removed has paid bookings, tick <b>Auto-refund via Razorpay</b> and they'll be refunded before deletion (3–5 business days per Razorpay). If a refund fails, nothing is deleted — you can retry.
          </div>

          <div className="mt-3">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Hostels to KEEP ({resetKeepIds.size} selected)</div>
            <div className="border border-slate-200 rounded-xl max-h-64 overflow-y-auto divide-y divide-slate-100">
              {allHostels.length === 0 && <div className="p-4 text-sm text-slate-500">No hostels.</div>}
              {allHostels.map((h) => (
                <label key={h.id} className={`flex items-center gap-3 p-3 hover:bg-orange-50 cursor-pointer ${resetKeepIds.has(h.id) ? 'bg-emerald-50' : ''}`} data-testid={`reset-keep-${h.id}`}>
                  <Checkbox
                    checked={resetKeepIds.has(h.id)}
                    onCheckedChange={(v) => {
                      const next = new Set(resetKeepIds);
                      if (v) next.add(h.id); else next.delete(h.id);
                      setResetKeepIds(next);
                    }}
                  />
                  <img src={h.images?.[0]} alt="" className="w-10 h-10 rounded object-cover bg-slate-100" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{h.name} <span className="text-xs text-slate-500 font-normal">· {h.city}</span></div>
                    <div className="text-[11px] text-slate-500">Owner: {h.owner_name || '—'} · {h.owner_email || '—'} · <StatusPill value={h.status} /></div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Button
                data-testid="reset-select-none"
                type="button" variant="outline" size="sm"
                onClick={() => setResetKeepIds(new Set())}
                className="rounded-full h-8 text-xs"
              >Select none</Button>
              <Button
                data-testid="reset-select-all"
                type="button" variant="outline" size="sm"
                onClick={() => setResetKeepIds(new Set(allHostels.map(h => h.id)))}
                className="rounded-full h-8 text-xs"
              >Select all</Button>
            </div>
          </div>

          <label className="mt-4 flex items-start gap-2 cursor-pointer">
            <Checkbox
              data-testid="reset-force-refund"
              checked={resetForce}
              onCheckedChange={(v) => { setResetForce(!!v); if (v) setResetSkipRefunds(false); }}
            />
            <span className="text-sm text-slate-700">
              <b>Auto-refund all paid bookings</b> on removed hostels through Razorpay before deletion.
              Required if any paid bookings exist. Manual payments (non-Razorpay) get a MANUAL-<i>xxx</i> refund reference.
            </span>
          </label>

          <label className="mt-3 flex items-start gap-2 cursor-pointer bg-rose-50 border border-rose-300 rounded-lg p-3">
            <Checkbox
              data-testid="reset-skip-refunds"
              checked={resetSkipRefunds}
              onCheckedChange={(v) => { setResetSkipRefunds(!!v); if (v) setResetForce(false); }}
            />
            <span className="text-sm text-rose-900">
              <b className="text-rose-700">Skip refunds (nuclear delete)</b> — hard-delete every booking without calling Razorpay.
              <span className="block text-[11px] text-rose-800/80 mt-1">
                Use ONLY for demo / test data. Real customers WILL NOT be refunded through this path — you must refund them manually outside the platform.
              </span>
            </span>
          </label>

          <div className="mt-4">
            <Label htmlFor="reset-confirm" className="text-xs uppercase tracking-widest font-bold text-slate-500">Type RESET to confirm</Label>
            <Input
              id="reset-confirm"
              data-testid="reset-confirm-input"
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              placeholder="RESET"
              className="mt-1"
            />
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting} className="rounded-full">Cancel</Button>
            <Button
              data-testid="reset-platform-confirm"
              onClick={runReset}
              disabled={resetting || resetConfirmText !== "RESET"}
              className="rounded-full bg-rose-700 hover:bg-rose-800 text-white gap-2"
            >
              {resetting ? <><Loader2 className="animate-spin" size={14} /> Purging…</> : <><Trash2 size={14} /> Purge platform data</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* --- Mark payout as paid dialog --- */}
      <Dialog open={!!payTarget} onOpenChange={(v) => !v && setPayTarget(null)}>
        <DialogContent className="max-w-md" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Mark payout as paid</DialogTitle>
            <DialogDescription>
              Confirm you&apos;ve transferred <span className="font-bold text-slate-900">{fmtINR(Math.round(payTarget?.net_amount || 0))}</span> to <span className="font-semibold">{payTarget?.owner_name}</span> and enter the transaction reference.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label htmlFor="utr">UTR / Transaction reference *</Label>
              <Input id="utr" data-testid="payout-utr-input" value={payUtr} onChange={(e) => setPayUtr(e.target.value)} placeholder="e.g. UPI 4123456789 or IMPS 202601251234" className="uppercase" />
              <p className="text-[10px] text-slate-500 mt-1">Shown to the owner in their withdrawal history for their bank records.</p>
            </div>
            <div>
              <Label htmlFor="note">Internal note (optional)</Label>
              <Textarea id="note" rows={2} data-testid="payout-note-input" value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Any internal comment…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)} disabled={payLoading} className="rounded-full">Cancel</Button>
            <Button
              data-testid="payout-confirm-btn"
              disabled={payLoading || !payUtr.trim()}
              onClick={async () => {
                setPayLoading(true);
                try {
                  await api.post(`/admin/withdrawals/${payTarget.id}/mark-paid`, { utr: payUtr.trim(), note: payNote });
                  toast.success("Payout marked as paid — owner notified in history");
                  setPayTarget(null); setPayUtr(""); setPayNote("");
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Mark-paid failed");
                } finally { setPayLoading(false); }
              }}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            >
              {payLoading ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : "Confirm paid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Document (Aadhaar/PAN) fullscreen viewer --- */}
      <Dialog open={!!docPreview} onOpenChange={(v) => !v && setDocPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="doc-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {docPreview?.label} · <span className="font-normal text-slate-500 text-base">{docPreview?.user}</span>
            </DialogTitle>
            <DialogDescription>
              Verify name, DOB, photo & document number are readable before approving.
            </DialogDescription>
          </DialogHeader>
          {docPreview?.url && (
            <div className="max-h-[75vh] overflow-auto bg-slate-100 rounded-xl p-2">
              <img src={docPreview.url} alt={docPreview.label} className="w-full h-auto rounded-lg" />
            </div>
          )}
          <DialogFooter>
            <a href={docPreview?.url} target="_blank" rel="noopener noreferrer" className="text-xs text-orange-800 hover:underline mr-auto">Open in new tab</a>
            <Button variant="outline" onClick={() => setDocPreview(null)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Add hostel dialog --- */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-add-hostel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add hostel (admin)</DialogTitle>
            <DialogDescription>Goes live immediately (auto-approved). Optionally assign to an existing owner account.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="an">Name *</Label>
                <Input id="an" data-testid="new-hostel-name" value={newHostel.name} onChange={(e) => setNewHostel({ ...newHostel, name: e.target.value })} placeholder="Krishna Boys Hostel" />
              </div>
              <div>
                <Label htmlFor="ac">City *</Label>
                <Input id="ac" data-testid="new-hostel-city" value={newHostel.city} onChange={(e) => setNewHostel({ ...newHostel, city: e.target.value })} placeholder="Kota" />
              </div>
            </div>
            <div>
              <Label htmlFor="aa">Address *</Label>
              <Input id="aa" data-testid="new-hostel-address" value={newHostel.address} onChange={(e) => setNewHostel({ ...newHostel, address: e.target.value })} placeholder="Talwandi, near Allen Career Institute" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="ag">Gender *</Label>
                <select id="ag" data-testid="new-hostel-gender" value={newHostel.gender} onChange={(e) => setNewHostel({ ...newHostel, gender: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="boys">Boys</option>
                  <option value="girls">Girls</option>
                  <option value="coed">Co-ed</option>
                </select>
              </div>
              <div>
                <Label htmlFor="ap">₹ per bed / night *</Label>
                <Input id="ap" type="number" min={100} max={5000} data-testid="new-hostel-price" value={newHostel.price_per_bed} onChange={(e) => setNewHostel({ ...newHostel, price_per_bed: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="ab">Total beds *</Label>
                <Input id="ab" type="number" min={1} data-testid="new-hostel-beds" value={newHostel.total_beds} onChange={(e) => setNewHostel({ ...newHostel, total_beds: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="aoe">Owner email <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input id="aoe" data-testid="new-hostel-owner-email" value={newHostel.owner_email} onChange={(e) => setNewHostel({ ...newHostel, owner_email: e.target.value })} placeholder="owner@example.com" />
                <p className="text-[10px] text-slate-500 mt-1">Leave blank to keep yourself as owner.</p>
              </div>
              <div>
                <Label htmlFor="aop">Owner contact phone</Label>
                <Input id="aop" data-testid="new-hostel-owner-phone" value={newHostel.owner_phone} onChange={(e) => setNewHostel({ ...newHostel, owner_phone: e.target.value })} placeholder="9876500000" />
              </div>
            </div>

            <div>
              <Label htmlFor="aam">Amenities (comma-separated)</Label>
              <Input id="aam" data-testid="new-hostel-amenities" value={newHostel.amenities} onChange={(e) => setNewHostel({ ...newHostel, amenities: e.target.value })} placeholder="wifi, food, study, laundry" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t pt-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="new-hostel-ac" checked={newHostel.ac_available} onChange={(e) => setNewHostel({ ...newHostel, ac_available: e.target.checked })} />
                Offer A/C rooms
              </label>
              {newHostel.ac_available && (
                <div>
                  <Label>A/C price/bed (₹, must be &gt; base)</Label>
                  <Input type="number" min={0} data-testid="new-hostel-ac-price" value={newHostel.ac_price_per_bed} onChange={(e) => setNewHostel({ ...newHostel, ac_price_per_bed: e.target.value })} />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t pt-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="new-hostel-meal" checked={newHostel.meal_available} onChange={(e) => setNewHostel({ ...newHostel, meal_available: e.target.checked })} />
                Offer meal plan
              </label>
              {newHostel.meal_available && (
                <div>
                  <Label>Meal price/bed/day (₹100-250)</Label>
                  <Input type="number" min={100} max={250} data-testid="new-hostel-meal-price" value={newHostel.meal_price} onChange={(e) => setNewHostel({ ...newHostel, meal_price: e.target.value })} />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="ad">Description</Label>
              <Textarea id="ad" rows={3} data-testid="new-hostel-desc" value={newHostel.description} onChange={(e) => setNewHostel({ ...newHostel, description: e.target.value })} placeholder="Clean, safe hostel with mess & 24/7 security." />
            </div>

            <div>
              <Label htmlFor="adt">Property document type</Label>
              <select id="adt" value={newHostel.property_document_type} onChange={(e) => setNewHostel({ ...newHostel, property_document_type: e.target.value })} className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="ownership">Ownership</option>
                <option value="lease">Lease</option>
                <option value="poa">Power of Attorney</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving} className="rounded-full">Cancel</Button>
            <Button
              data-testid="admin-create-hostel-btn"
              onClick={saveNewHostel}
              disabled={saving}
              className="rounded-full bg-orange-800 hover:bg-orange-900 text-white gap-2"
            >
              {saving ? <><Loader2 className="animate-spin" size={14} /> Creating…</> : "Add hostel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
